import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const initialAperturaForm = {
  otroGuardia: '',
  observaciones: '',
  itemsSeleccionados: {}
};

const Relevos = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [cierreObservaciones, setCierreObservaciones] = useState('');
  const [aperturaForm, setAperturaForm] = useState(initialAperturaForm);
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para firma del vigilante saliente
  const [firmaArchivo, setFirmaArchivo] = useState(null);
  const [firmaPreview, setFirmaPreview] = useState('');
  const [isFirmaModalOpen, setIsFirmaModalOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [firmaTieneTrazo, setFirmaTieneTrazo] = useState(false);
  const canvasRef = useRef(null);

  const canLoadRelevos = useMemo(() => {
    return Boolean(user?.id_generado);
  }, [user]);

  const resetForms = useCallback(() => {
    setCierreObservaciones('');
    setAperturaForm({
      otroGuardia: '',
      observaciones: '',
      itemsSeleccionados: {}
    });
    // Limpiar firma - usar ref para evitar dependencia
    setFirmaArchivo((prev) => {
      if (prev) {
        // El preview se limpiará con el estado
      }
      return null;
    });
    setFirmaPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
    setIsFirmaModalOpen(false);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
  }, []);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) {
        return;
      }

      if (!canLoadRelevos) {
        setError('Tu usuario no tiene un puesto asignado (id_generado) para gestionar relevos.');
        setStatus(null);
        setLoading(false);
        return;
      }

      setError('');
      setSuccess('');
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const url = new URL(getApiUrl('RELEVOS', 'STATUS'));
        url.searchParams.set('username', user.username);
        url.searchParams.set('rol', user.rol || '');
        url.searchParams.set('idGenerado', user.id_generado);
        if (user.appid) {
          url.searchParams.set('appid', user.appid);
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();

        if (response.ok && data.success) {
          setStatus(data.data);
          resetForms();
        } else {
          setStatus(null);
          setError(data.error || 'No fue posible obtener el estado del relevo.');
        }
      } catch (err) {
        console.error('Error cargando el estado de relevos:', err);
        setStatus(null);
        setError('Error de conexión al cargar el estado del relevo. Verifica tu red o el servidor.');
      } finally {
        if (showLoader) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [user, canLoadRelevos, resetForms]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Inicializar canvas cuando se abre el modal
  useEffect(() => {
    if (isFirmaModalOpen && canvasRef.current) {
      // Pequeño delay para asegurar que el canvas esté renderizado
      const timer = setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 2.5;
            ctx.lineCap = 'round';
            setFirmaTieneTrazo(false);
            setIsDrawing(false);
            console.log('Canvas inicializado correctamente');
          }
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isFirmaModalOpen]);

  // Limpiar preview al desmontar
  useEffect(() => {
    return () => {
      if (firmaPreview) {
        URL.revokeObjectURL(firmaPreview);
      }
    };
  }, [firmaPreview]);

  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX ?? 0) - rect.left) * (canvas.width / rect.width);
    const y = ((event.clientY ?? 0) - rect.top) * (canvas.height / rect.height);
    return { x, y };
  }, []);

  const handleOpenFirmaModal = useCallback(() => {
    if (submitting) return;
    setError('');
    setIsFirmaModalOpen(true);
  }, [submitting]);

  const handleCloseFirmaModal = useCallback(() => {
    setIsFirmaModalOpen(false);
    setIsDrawing(false);
    setFirmaTieneTrazo(false);
  }, []);

  const handleFirmaPointerDown = useCallback((event) => {
    if (!isFirmaModalOpen || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setFirmaTieneTrazo(true);
  }, [getCanvasCoordinates, isFirmaModalOpen]);

  const handleFirmaPointerMove = useCallback((event) => {
    if (!isDrawing || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCanvasCoordinates, isDrawing]);

  const handleFirmaPointerUp = useCallback((event) => {
    if (!canvasRef.current) return;
    event?.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
  }, []);

  const handleClearFirma = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, []);

  const handleGuardarFirma = useCallback(() => {
    if (!canvasRef.current) return;
    if (!firmaTieneTrazo) {
      setError('Por favor registra la firma del vigilante saliente.');
      return;
    }
    setError('');
    
    try {
      canvasRef.current.toBlob((blob) => {
        if (!blob) {
          setError('No se pudo generar la firma. Intenta nuevamente.');
          return;
        }
        
        // Revocar URL anterior si existe usando callback
        setFirmaPreview((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          const previewUrl = URL.createObjectURL(blob);
          return previewUrl;
        });
        
        const file = new File([blob], `firma_vigilante_${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        
        // Actualizar estados de forma síncrona - NO guardar aún, solo previsualizar
        setFirmaArchivo(file);
        setIsFirmaModalOpen(false);
        setIsDrawing(false);
        
        // NO mostrar mensaje de éxito aquí - solo cerrar el modal y mostrar preview
        console.log('Firma capturada, mostrando preview...');
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error generando firma:', error);
      setError('No se pudo generar la firma. Intenta nuevamente.');
    }
  }, [firmaTieneTrazo]);

  const handleEliminarFirma = useCallback(() => {
    if (firmaPreview) {
      URL.revokeObjectURL(firmaPreview);
    }
    setFirmaArchivo(null);
    setFirmaPreview('');
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, [firmaPreview]);

  const handleCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
    setError('');
  }, []);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
  }, []);

  const handleSubmitCierre = async (event) => {
    event.preventDefault();
    if (!status) return;

    // Validar que las observaciones sean requeridas
    if (!cierreObservaciones || !cierreObservaciones.trim()) {
      setError('Por favor ingresa las observaciones del turno saliente.');
      return;
    }

    // Validar que la firma sea requerida
    if (!firmaArchivo || !firmaPreview) {
      setError('Por favor registra la firma del vigilante saliente antes de cerrar el turno.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('idGenerado', status.idGenerado);
      formData.append('observaciones', cierreObservaciones);
      formData.append('turno', status.turno);
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      
      if (firmaArchivo) {
        formData.append('firma', firmaArchivo);
      }

      const response = await fetch(getApiUrl('RELEVOS', 'CIERRE'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message || 'Turno cerrado correctamente.');
        resetForms();
        await fetchStatus(false);
      } else {
        setError(data.error || 'No fue posible cerrar el turno.');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Error cerrando turno:', err);
      setError('Error de conexión al cerrar el turno.');
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleItemToggle = (itemId) => {
    setAperturaForm((prev) => ({
      ...prev,
      itemsSeleccionados: {
        ...prev.itemsSeleccionados,
        [itemId]: !prev.itemsSeleccionados[itemId]
      }
    }));
  };

  const handleSubmitApertura = async (event) => {
    event.preventDefault();
    if (!status) return;

    const guardiaCedula = aperturaForm.otroGuardia.trim().replace(/\D/g, '');

    if (!guardiaCedula) {
      setError('Debes ingresar la cédula del guardia entrante.');
      setShowErrorModal(true);
      return;
    }

    // Validar que las observaciones sean requeridas
    if (!aperturaForm.observaciones || !aperturaForm.observaciones.trim()) {
      setError('Por favor ingresa las observaciones del guardia entrante.');
      setShowErrorModal(true);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    const itemsPayload = (status.items || []).map((item) => ({
      id: item.id,
      descripcion: item.descripcion,
      baseDescripcion: item.baseDescripcion,
      recibido: Boolean(aperturaForm.itemsSeleccionados[item.id])
    }));

    try {
      const response = await fetch(getApiUrl('RELEVOS', 'APERTURA'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idGenerado: status.idGenerado,
          turno: status.turno,
          fechaHistorial: status.fechaHistorial,
          guardiaCedula: guardiaCedula,
          observaciones: aperturaForm.observaciones,
          items: itemsPayload,
          username: user?.username || '',
          appid: user?.appid || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message || 'Relevo registrado correctamente.');
        setShowSuccessModal(true);
        resetForms();
        await fetchStatus(false);
      } else {
        setError(data.error || 'No fue posible registrar el relevo.');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Error registrando relevo:', err);
      setError('Error de conexión al registrar el relevo.');
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderEstadoGeneral = () => {
    if (!status) {
      return null;
    }

    const horaRelevoTexto = status.horaRelevo
      ? new Date(status.horaRelevo).toLocaleTimeString('es-EC', {
          hour: '2-digit',
          minute: '2-digit'
        })
      : null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Turno actual</p>
          <p className="text-lg font-semibold text-gray-900">{status.turno}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Fecha del turno</p>
          <p className="text-lg font-semibold text-gray-900">{status.fechaHistorial}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Ventana de relevo</p>
          <p className={`text-lg font-semibold ${status.ventanaFlag === 'OK' ? 'text-green-600' : 'text-amber-600'}`}>
            {status.ventanaFlag === 'OK' ? 'Activa' : 'Fuera de horario'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Observación de cierre esperado</p>
          <p className="text-lg font-semibold text-gray-900">{status.observacionCierre}</p>
        </div>
        {horaRelevoTexto && (
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-sm text-gray-500">Último relevo registrado</p>
            <p className="text-lg font-semibold text-gray-900">{horaRelevoTexto}</p>
          </div>
        )}
      </div>
    );
  };

  const renderNoDisponible = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No es posible relevar en este momento</h3>
      <p className={`text-gray-600 ${status?.motivo?.includes('El puesto fue relevado') ? 'text-blue-600 font-semibold' : ''}`}>
        {status?.motivo || 'Consulta nuevamente más tarde.'}
        {status?.horaRelevo && (
          <span className="block text-sm text-gray-500 mt-1">
            Último relevo registrado a las{' '}
            {new Date(status.horaRelevo).toLocaleTimeString('es-EC', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        )}
      </p>
      <button
        type="button"
        onClick={() => fetchStatus(false)}
        className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg shadow hover:bg-indigo-700 transition-colors duration-200"
      >
        Actualizar estado
      </button>
    </div>
  );

  const renderFormularioCierre = () => (
    <form onSubmit={handleSubmitCierre} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 shadow">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Cierre del parte</h3>
        <p className="text-gray-600 text-sm">
          Registra las observaciones antes de cerrar el turno actual.
        </p>
      </div>

      <div>
        <label htmlFor="observaciones-cierre" className="block text-sm font-medium text-gray-700 mb-1">
          Observaciones <span className="text-red-500">*</span>
        </label>
        <textarea
          id="observaciones-cierre"
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Ingresa observaciones del turno saliente"
          value={cierreObservaciones}
          onChange={(e) => setCierreObservaciones(e.target.value)}
          disabled={submitting}
          required
        />
        {!cierreObservaciones || !cierreObservaciones.trim() ? (
          <p className="mt-1 text-xs text-amber-600">Las observaciones son obligatorias.</p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Firma del vigilante saliente <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleOpenFirmaModal}
              className="inline-flex justify-center items-center w-full sm:w-[140px] px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-60"
              disabled={submitting}
            >
              {firmaPreview ? 'Cambiar firma' : 'Capturar firma'}
            </button>
            {firmaArchivo && firmaPreview && (
              <button
                type="button"
                onClick={handleEliminarFirma}
                className="inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                disabled={submitting}
              >
                Eliminar firma
              </button>
            )}
          </div>
          {firmaPreview && firmaArchivo ? (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-2 font-medium">Previsualización de la firma:</p>
              <div className="inline-block p-2 bg-gray-50 rounded-lg border border-gray-200">
                <img
                  key={firmaPreview}
                  src={firmaPreview}
                  alt="Firma del vigilante saliente"
                  className="max-w-full h-auto max-h-48 rounded border border-gray-300 bg-white"
                  onLoad={() => {
                    console.log('Preview de firma cargado correctamente');
                  }}
                  onError={(e) => {
                    console.error('Error cargando preview de firma:', e);
                    setError('Error mostrando la previsualización de la firma.');
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800 font-medium">
                ⚠️ La firma es obligatoria antes de cerrar el turno.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Solicita al vigilante saliente que firme en el canvas.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end space-x-3">
        <button
          type="button"
          onClick={resetForms}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          disabled={submitting}
        >
          Limpiar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? 'Guardando...' : 'Cerrar Turno'}
        </button>
      </div>
    </form>
  );

  const renderFormularioApertura = () => (
    <form onSubmit={handleSubmitApertura} className="bg-white border border-gray-200 rounded-xl p-6 space-y-6 shadow">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Registro de relevo</h3>
        <p className="text-gray-600 text-sm">
          Confirma la recepción de los elementos y registra al guardia entrante.
        </p>
      </div>

      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Cantidad &amp; Elemento
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Recibido
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(status?.items || []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-sm text-gray-700">{item.descripcion}</td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      checked={Boolean(aperturaForm.itemsSeleccionados[item.id])}
                      onChange={() => handleItemToggle(item.id)}
                      disabled={submitting}
                    />
                    <span className="text-sm text-gray-600">Recibido</span>
                  </label>
                </td>
              </tr>
            ))}
            {status?.items?.length === 0 && (
              <tr>
                <td colSpan={2} className="px-4 py-4 text-center text-sm text-gray-500">
                  No hay ítems pendientes de entrega para este relevo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

          <div>
            <label htmlFor="otro-guardia" className="block text-sm font-medium text-gray-700 mb-1">
          Cédula del guardia entrante <span className="text-red-500">*</span>
            </label>
            <input
              id="otro-guardia"
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ingresa la cédula del guardia"
              value={aperturaForm.otroGuardia}
              onChange={(e) =>
                setAperturaForm((prev) => ({ ...prev, otroGuardia: e.target.value }))
              }
              disabled={submitting}
              required
            />
      </div>

      <div>
        <label htmlFor="observaciones-apertura" className="block text-sm font-medium text-gray-700 mb-1">
          Observaciones del guardia entrante <span className="text-red-500">*</span>
        </label>
        <textarea
          id="observaciones-apertura"
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Registra observaciones relevantes"
          value={aperturaForm.observaciones}
          onChange={(e) =>
            setAperturaForm((prev) => ({ ...prev, observaciones: e.target.value }))
          }
          disabled={submitting}
          required
        />
        {(!aperturaForm.observaciones || !aperturaForm.observaciones.trim()) && (
          <p className="mt-1 text-xs text-amber-600">Las observaciones son obligatorias.</p>
        )}
      </div>

      <div className="flex items-center justify-end space-x-3">
        <button
          type="button"
          onClick={resetForms}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          disabled={submitting}
        >
          Limpiar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? 'Guardando...' : 'Registrar Relevo'}
        </button>
      </div>
    </form>
  );

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Relevos</h1>
            <p className="text-gray-600">Gestiona la apertura y cierre de turnos para tu puesto asignado.</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => fetchStatus(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
              disabled={loading || refreshing || submitting}
            >
              {refreshing ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Actualizando...</span>
                </span>
              ) : (
                'Actualizar estado'
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando información del relevo...</span>
            </div>
          </div>
        ) : (
          <>
            {status && renderEstadoGeneral()}

            {status && status.puedeRelevar !== 'SI' && renderNoDisponible()}

            {status && status.puedeRelevar === 'SI' && status.requiereCierre && renderFormularioCierre()}

            {status && status.puedeRelevar === 'SI' && !status.requiereCierre && renderFormularioApertura()}
          </>
        )}

        {/* Modal de error */}
        {showErrorModal && Boolean(error) && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-red-100 bg-red-600 text-white">
                <h3 className="text-lg font-semibold">No se pudo completar la acción</h3>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-gray-700">{error}</p>
                <p className="text-xs text-gray-500">Revisa la información solicitada y vuelve a intentarlo.</p>
              </div>
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-right">
                <button
                  type="button"
                  onClick={handleCloseErrorModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de éxito */}
        {showSuccessModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseSuccessModal();
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de éxito */}
              <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Registro exitoso</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Relevo registrado correctamente</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 font-medium">
                    {success || 'El relevo ha sido registrado exitosamente en el sistema.'}
                  </p>
                </div>

                {/* Indicador visual */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Registro confirmado</span>
                </div>
              </div>

              {/* Footer con botón */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseSuccessModal}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de firma - Fuera del form para evitar recarga */}
        {isFirmaModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseFirmaModal();
              }
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Registrar firma del vigilante saliente</h3>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCloseFirmaModal();
                  }}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                  aria-label="Cerrar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div className="border-2 border-gray-300 rounded-lg bg-white p-2">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={300}
                    className="w-full border border-gray-200 rounded cursor-crosshair touch-none"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      handleFirmaPointerDown(e);
                    }}
                    onPointerMove={(e) => {
                      e.preventDefault();
                      handleFirmaPointerMove(e);
                    }}
                    onPointerUp={(e) => {
                      e.preventDefault();
                      handleFirmaPointerUp(e);
                    }}
                    onPointerLeave={(e) => {
                      e.preventDefault();
                      handleFirmaPointerUp(e);
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearFirma();
                    }}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Limpiar
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGuardarFirma();
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Guardar firma
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Relevos;

